import { Drawer, List, useMediaQuery, useTheme, Box, Typography } from '@mui/material';
import * as urls from 'routes/config';
import SidebarItem from './SidebarItem';
import SidebarSectionLabel from './SidebarSectionLabel';
import TeamIcon from 'assets/icons/TeamIcon';
import ConfigureIcon from 'assets/icons/ConfigureIcon';
import IntegrationIcon from 'assets/icons/IntegrationIcon';
import BillingIcon from 'assets/icons/BillingIcon';
import RevenueIcon from '@/assets/icons/RevenueIcon';
import BusinessIcon from '@/assets/icons/BusinessIcon';
import AuditTrailsIcon from '@/assets/icons/AuditTrailsIcon';
import { useGetAgencyOrganizations } from '@/apis/agency/organizations/hook';
import { MdContentCopy } from 'react-icons/md';
import copy from 'copy-to-clipboard';
import VoiceEnginesIcon from '@/assets/icons/VoiceEnginesIcon';
import BusinessDropdown from './BusinessDropdown';
import { RxCross2 } from 'react-icons/rx';
import OnboardingFormsIcon from '@/assets/icons/OnboardingFormsIcon';
import IntraOfficeNotesIcon from '@/assets/icons/IntraOfficeNotesIcon';
import SystemSentimentIcon from '@/assets/icons/SystemSentimentIcon';
import TelephonyIcon from '@/assets/icons/TelephonyIcon';

interface Props {
  isMenuOpen?: boolean;
  handleClose?: () => void;
  isPausedPayment?: boolean;
}

export default function AgencySidebar({ isMenuOpen, handleClose, isPausedPayment }: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { data: agencyOrganizationData } = useGetAgencyOrganizations();

  return (
    <Drawer
      variant={isMediumScreen ? 'temporary' : 'permanent'}
      open={isMediumScreen ? isMenuOpen : true}
      onClose={handleClose}
      anchor='left'
      sx={{
        width: 240,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: 240,
          height: isMediumScreen ? '100%' : 'calc(100% - 44px)',
          boxSizing: 'border-box',
          top: isMediumScreen ? 0 : '64px',
          backgroundColor: theme.palette.background.paper,
          display: 'flex',
          flexDirection: 'column'
        }
      }}
      ModalProps={{
        keepMounted: true
      }}
    >
      {isMediumScreen && (
        <Box
          px={2}
          pt={2}
          borderBottom='1px solid #EAEAEA'
          display='flex'
          justifyContent='space-between'
          alignItems='center'
        >
          <Box display='flex' alignItems='center' justifyContent='space-between'>
            <BusinessDropdown />
          </Box>
          <RxCross2 size={16} onClick={handleClose} />
        </Box>
      )}
      <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
        {drawerContent(Boolean(agencyOrganizationData?.organization_plan_id), isPausedPayment)}
      </Box>
      <Box
        px={1}
        py={1}
        bgcolor='#F8F9FA'
        m={2.8}
        borderRadius={1}
        display='flex'
        justifyContent='space-between'
        gap={2}
        alignItems='center'
      >
        <Typography fontSize='14px'>Agency Code</Typography>
        <Box
          display='flex'
          gap={0.5}
          alignItems='center'
          sx={{ cursor: 'pointer' }}
          onClick={() => copy(agencyOrganizationData?.provider_id || '')}
        >
          <Typography fontSize='14px' color='primary'>
            {agencyOrganizationData?.provider_id}
          </Typography>
          <MdContentCopy />
        </Box>
      </Box>
    </Drawer>
  );
}

const drawerContent = (billingEnabled: boolean, isPausedPayment?: boolean) => (
  <List disablePadding sx={{ py: 0.5 }}>
    <SidebarSectionLabel label='Customers' />
    <SidebarItem
      to={urls.agency.businesses}
      icon={<BusinessIcon />}
      label='Businesses'
      end={true}
      disabled={isPausedPayment}
    />
    <SidebarItem
      to={urls.agency.onboardingForms}
      icon={<OnboardingFormsIcon />}
      label='Onboarding'
      disabled={isPausedPayment}
    />

    <SidebarSectionLabel label='Insights' />
    <SidebarItem
      to={urls.agency.systemSentiment}
      icon={<SystemSentimentIcon />}
      label='System Sentiment'
      end={true}
      disabled={isPausedPayment}
    />
    <SidebarItem to={urls.agency.revenue} icon={<RevenueIcon />} label='Revenue' disabled={isPausedPayment} />

    <SidebarSectionLabel label='Services' />
    <SidebarItem
      to={urls.agency.voiceEngines}
      icon={<VoiceEnginesIcon />}
      label='Voice Engines'
      disabled={isPausedPayment}
    />
    <SidebarItem to={urls.agency.telephony} icon={<TelephonyIcon />} label='Telephony' disabled={isPausedPayment} />
    <SidebarItem
      to={urls.agency.integrations}
      icon={<IntegrationIcon />}
      label='Integrations'
      disabled={isPausedPayment}
    />

    <SidebarSectionLabel label='Platform' />
    <SidebarItem to={urls.agency.team} icon={<TeamIcon />} label='Team' disabled={isPausedPayment} />
    <SidebarItem
      to={urls.agency.intraofficeNotes}
      icon={<IntraOfficeNotesIcon />}
      label='Team Notes'
      disabled={isPausedPayment}
    />
    {billingEnabled && <SidebarItem to={urls.agency.billing} icon={<BillingIcon />} label='Billing' />}
    <SidebarItem
      to={urls.agency.auditTrails}
      icon={<AuditTrailsIcon />}
      label='Audit Trails'
      disabled={isPausedPayment}
    />
    <SidebarItem
      to={urls.agency.configuration}
      icon={<ConfigureIcon />}
      label='Configuration'
      disabled={isPausedPayment}
    />
  </List>
);
